// The lock screen (design/screens/signin.html, right card): the user's name and Control Id only, PIN or password to resume,
// Switch user signs out (UC-IDENTITY-003). A wrong PIN is refused generically; a locked Control Id still unlocks by password,
// and so does a session whose PIN an administrator reset meanwhile — its one-time PIN unlocks nothing (E5).
// Enter submits the PIN; it auto-submits only when the tenant's minimum and maximum PIN lengths are equal, so a user whose PIN
// is longer than the minimum can finish typing it (owner, 15 Sep 2026). The lengths travel with the locked `who`, because the
// session view is refused while locked; without them the screen assumes the widest policy and never auto-submits.
import { PIN_MAX_LENGTH } from "@visionx/contracts";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { auth } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { formatTime } from "../lib/format";
import { loadSession, signOut, useSession } from "../session/store";
import { Alert, Avatar, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";

/** The tenant default minimum (tenancy settings `security.pinMinLength`), used only when the locked `who` carries no lengths. */
const PIN_MIN_LENGTH_DEFAULT = 4;

export function Lock() {
  const s = useSession();
  const who = s.status === "locked" ? s.who : null;
  const [mode, setMode] = useState<"pin" | "password">("pin");
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "crit" | "warn"; text: ReactNode } | null>(null);
  const [lockedAt] = useState(() => new Date());
  const pinRef = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);
  const minLength = who?.pinMinLength ?? PIN_MIN_LENGTH_DEFAULT;
  const maxLength = who?.pinMaxLength ?? PIN_MAX_LENGTH;
  const fixedLength = minLength === maxLength;

  useEffect(() => { pinRef.current?.focus(); }, [mode]);

  const unlock = async (body: { pin?: string; password?: string }) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setMessage(null);
    try {
      await auth.unlock(body);
      await loadSession({ credentialAccepted: true });
    } catch (err) {
      setPin("");
      setPassword("");
      if (isApiError(err) && err.code === "STEP_UP_LOCKED") {
        const until = typeof err.meta.lockedUntil === "string" ? formatTime(err.meta.lockedUntil) : null;
        setMessage({ tone: "crit", text: `Your Control PIN is locked${until ? ` until ${until}` : ""}. Unlock with your password.` });
        setMode("password");
      } else if (isApiError(err) && err.code === "ACCOUNT_LOCKED") {
        // The sign-in page's words, with the time the lock ends — never the server's own text (UC-IDENTITY-003 E3, E4; testing of
        // 12 Oct 2026, finding 3). The PIN is a separate credential and may still unlock (BR-IDENTITY-007).
        const until = typeof err.meta.lockedUntil === "string" ? err.meta.lockedUntil : new Date(Date.now() + 15 * 60_000).toISOString();
        setMessage({ tone: "crit", text: <><b>Account locked.</b> Too many failed attempts. Try again after {formatTime(until)}, or reset your password.</> });
      } else if (isApiError(err) && err.code === "AUTHENTICATION_FAILED" && err.meta.reason === "pin_must_change") {
        // The one-time PIN of an in-person reset unlocks nothing (BR-IDENTITY-035): the password does, then PIN set-up follows.
        setMessage({ tone: "warn", text: "Your PIN was reset and has to be replaced. Unlock with your password, then choose a new PIN." });
        setMode("password");
      } else if (isApiError(err) && err.code === "AUTHENTICATION_FAILED") {
        setMessage({ tone: "crit", text: "Not accepted. Try again." });
      } else if (isApiError(err) && err.code === "AUTHENTICATION_REQUIRED") {
        setMessage({ tone: "warn", text: "Your session has ended. Sign in again." });
        await signOut();
      } else {
        setMessage({ tone: "crit", text: describeError(err) });
      }
    } finally {
      submitting.current = false;
      setBusy(false);
      setTimeout(() => pinRef.current?.focus(), 0);
    }
  };

  // Auto-submit only when every PIN has the same length: the last digit is then the whole PIN.
  useEffect(() => {
    if (mode === "pin" && fixedLength && pin.length === maxLength && !busy) void unlock({ pin });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === "pin") {
      if (pin.length >= minLength) void unlock({ pin });
      else setMessage({ tone: "warn", text: fixedLength ? `Enter your ${minLength}-digit Control PIN.` : `Enter your Control PIN (${minLength} to ${maxLength} digits), then Enter.` });
    } else if (password) void unlock({ password });
  };
  // One box per digit of the minimum, and one more only for a digit typed past it: an empty box after the minimum read as a
  // demand for more digits from someone whose PIN was complete (staging, 7 Oct 2026). Enter or the button submits it.
  const digits = Math.max(minLength, pin.length);

  return (
    <section className="screen" id="s-signin" data-bare="true">
      <div className="content">
        <div className="wrap">
          <div className="cards">
            <div className="stack card">
              <form className="panel" onSubmit={onSubmit}>
                <div className="panel-head">
                  <Icon name="lock" width={16} height={16} />
                  <h2>Screen locked</h2>
                  <span className="meta right">{who?.branchName ? `${who.branchName} · ` : ""}locked {formatTime(lockedAt)}</span>
                </div>
                <div className="panel-body" style={{ padding: 24, gap: 16, alignItems: "center" }}>
                  <div className="whoami">
                    <Avatar initials={who?.initials ?? "?"} size="lg" />
                    <div className="strong">{who?.displayName ?? "Signed-in user"}</div>
                    <div className="small faint">{who ? <>{who.role} · Control ID <span className="mono">{who.controlIdDisplay}</span></> : "Enter your Control PIN or password"}</div>
                  </div>
                  {message && <Alert tone={message.tone}>{message.text}</Alert>}
                  {mode === "pin" ? (
                    <div className="pinbox" role="group" aria-label={fixedLength ? `Control PIN, ${minLength} digits` : `Control PIN, ${minLength} to ${maxLength} digits`}>
                      {Array.from({ length: digits }, (_, i) => <span key={i} className={`digit${i === pin.length ? " cur" : ""}`} aria-hidden="true">{i < pin.length ? "•" : ""}</span>)}
                      <input ref={pinRef} type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" aria-label="Control PIN" maxLength={maxLength} value={pin} disabled={busy} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, maxLength))} />
                    </div>
                  ) : (
                    <div className="field" style={{ width: "100%" }}>
                      <label htmlFor="lock-password">Password</label>
                      {/* Focused when the screen turns to the password — by the user, a locked PIN or a PIN that must be replaced — so it is typed at once. */}
                      <TextInput id="lock-password" type="password" autoComplete="current-password" value={password} disabled={busy} onChange={(e) => setPassword(e.target.value)} autoFocus />
                    </div>
                  )}
                  <div className="help">{mode === "pin" ? (fixedLength ? "Enter your Control PIN or password to resume" : "Type your Control PIN and press Enter, or use your password") : "Enter your password to resume"}</div>
                  <div className="row c">
                    {mode === "pin" && !fixedLength && <button className="btn primary" type="submit" disabled={busy}>Unlock</button>}
                    {mode === "pin" ? <button className="btn" type="button" onClick={() => setMode("password")}>Use password</button> : <button className="btn primary" type="submit" disabled={busy || !password}>Unlock</button>}
                    {mode === "password" && <button className="btn" type="button" onClick={() => { setMode("pin"); setMessage(null); }}>Use PIN</button>}
                    <button className="btn ghost" type="button" onClick={() => void signOut()}>Not you? Switch user</button>
                  </div>
                </div>
                <div className="panel-foot"><span className="small faint">Locked automatically after the practice's idle time — shorter on tablets. Unlocking is recorded in the audit log.</span></div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
